import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { AdminGuard } from 'app/core/auth/guards/admin.guard';
import { LayoutComponent } from 'app/layout/layout.component';

// @formatter:off

export const appRoutes: Route[] = [

    // Redirect empty path to '/sign-in'
    { path: '', pathMatch: 'full', redirectTo: 'sign-in' },

    // Redirect signed-in user to the main security summary home
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'home' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes') },
            { path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes') },
            { path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes') },
            { path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes') },
            // El alta de usuarios la hace un admin (Usuarios): no hay registro público
            { path: 'sign-up', pathMatch: 'full', redirectTo: 'sign-in' }
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes') },
            { path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes') }
        ]
    },

    // Protected application routes (requires authentication)
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            // Home/Dashboard - Main landing page after login
            { path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes') },

            // Projects
            {
                path: 'projects', loadChildren: () => import('app/views/projects/proyect.routes')
            },
            {
                path: 'ip-searcher', loadChildren: () => import('app/views/ip/ip.routes')
            },

            // Users (Admin-only route)
            {
                path: 'users',
                canActivate: [AdminGuard],
                canActivateChild: [AdminGuard],
                loadChildren: () => import('app/views/users/users.routes')
            },

            // F1 — Salud de captura ("mis señuelos están vivos")
            {
                path: 'capture-health',
                loadComponent: () => import('app/views/capture-health/capture-health.component').then(m => m.CaptureHealthComponent)
            },

            // Security Events
            {
                path: 'security-events',
                loadComponent: () => import('app/views/security-events/security-events.component').then(m => m.SecurityEventsComponent)
            },
            {
                // Enlaces antiguos a un evento: abren su reporte
                path: 'security-events/:id', pathMatch: 'full', redirectTo: 'security-events/:id/report'
            },
            {
                path: 'security-events/:id/report',
                loadComponent: () => import('app/views/security-events/security-event-report/security-event-report.component').then(m => m.SecurityEventReportComponent)
            },
            {
                // F3 — Ficha del evento en lenguaje claro (hermana del informe completo)
                path: 'security-events/:id/ficha',
                loadComponent: () => import('app/views/security-events/security-event-ficha/security-event-ficha.component').then(m => m.SecurityEventFichaComponent)
            },

            // Alerts (Unified Alerts System)
            {
                path: 'alerts',
                loadChildren: () => import('app/modules/alerts/alerts.routes')
            },

            // Threat Intelligence
            {
                path: 'threat-intelligence',
                loadChildren: () => import('app/views/threat-intelligence/threat-intelligence.routes')
            },

            // Detection Content
            {
                path: 'detection-content',
                loadChildren: () => import('app/modules/detection-content/detection-content.routes')
            },

            // Glossary
            {
                path: 'glossary',
                loadComponent: () => import('app/views/glossary/glossary.component').then(m => m.GlossaryComponent)
            },

            // Centro de ayuda: /ayuda y /ayuda/<seccion>#<articulo>
            {
                path: 'ayuda',
                loadComponent: () => import('app/views/help-center/help-center.component').then(m => m.HelpCenterComponent)
            },
            {
                path: 'ayuda/:section',
                loadComponent: () => import('app/views/help-center/help-center.component').then(m => m.HelpCenterComponent)
            },

            // Administration (Admin-only routes)
            {
                path: 'administration',
                canActivate: [AdminGuard],
                canActivateChild: [AdminGuard],
                children: [
                    { path: 'attack-types', loadChildren: () => import('app/modules/administration/attack-types/attack-types.routes') },
                    { path: 'attack-subtypes', loadChildren: () => import('app/modules/administration/attack-subtypes/attack-subtypes.routes') },
                    { path: 'honeytoken-types', loadChildren: () => import('app/modules/administration/honeytoken-types/honeytoken-types.routes') },
                    { path: 'domains', loadChildren: () => import('app/modules/administration/domains/domains.routes') },
                    { path: 'data-management', loadChildren: () => import('app/modules/administration/data-management/data-management.routes') },
                ]
            },

            // Honeytoken Management
            {
                path: 'honeytokens-management', children: [
                    { path: '', loadComponent: () => import('app/views/honeytokens/honeytoken-list/honeytoken-list.component').then(m => m.HoneytokenListComponent) },
                    { path: 'demo', loadComponent: () => import('app/components/honeytoken-demo/honeytoken-demo.component').then(m => m.HoneytokenDemoComponent) }
                ]
            },

            // Old Honeytoken Views (existing system)
            {
                path: 'honeytokens', loadChildren: () => import('app/views/honeytokens/honeyTokens.routes')
            },

            // Las páginas de demo de la plantilla Fuse (dashboards, apps, pages, ui, docs) se han quitado:
            // ya no son accesibles y sus módulos no se cargan.

            // Rutas antiguas o que usaban los guards: llevarlas a su destino real
            { path: 'dashboard', pathMatch: 'full', redirectTo: 'home' },
        ]
    },

    // Error pages (accessible to everyone - no auth required)
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: '404-not-found', loadChildren: () => import('app/modules/admin/pages/error/error-404/error-404.routes') }
        ]
    },

    // 404 & Catch all - Must be last
    { path: '**', redirectTo: '404-not-found' }
];
